/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useEffect } from 'react';
import { Form, Formik } from 'formik';
import { useActivation } from '@context/MobileOne';
import {
  ApiError,
  ButtonCancelActivation,
  ButtonCheckbox,
  ButtonPrimary,
  CardSupport,
  DropdownList,
  FocusFirstError,
  FormInputField,
  Notification,
  ScrollToTop,
  CMSLink
} from '@core/index';
import { AppPageContentWrapper } from '@part/AppPageContentWrapper';
import { tA11y } from '@utils/a11y/a11yHelpers';
import { sanitizeRichText } from '@utils/sanitizeHtml';
import {
  appAlert,
  appButtonType,
  appCardStyle,
  appCheckboxSize,
  appIconsPath,
  appLinkSize,
  appLinkStyle,
  validCharRegex
} from '@utils/globalConstant';

export function MNPActivation() {
  // Contexts
  const {
    isLoading,
    formError,
    phoneNumberActivationForm,
    setPhoneNumberActivationForm,
    phoneNumberValidationsWithMNP,
    simProvider,
    setStep3SubStep,
    simProviderError,
    areaCode,
    withMNPError,
    setWithMNPError
  } = useActivation();

  // State
  // const [isCheckboxChecked, setIsCheckboxChecked] = useState(false);
  // const [checkboxError, setCheckboxError] = useState(null);

  // const
  const headerContent = tA11y('ek_reg_with_mnp_hdl1');
  const provideLabelContent = tA11y('ek_global_prvdr_lbl');
  const providePlaceHolderContent = tA11y('ek_global_prvdr_plc');
  const oldNumberLabelContent = tA11y('ek_global_mnp_nmbr_lbl');
  const oldNumberPlaceHolderContent = tA11y('ek_global_mnp_nmbr_plc');
  const checkboxLabelContent = tA11y('ek_reg_with_mnp_chckbox_txt1');
  // const CheckboxErrorContent = tA11y('ek_reg_with_mnp_chckbox_err');

  const handleAPIErrorLink = () => {
    const firstInput = document.querySelector('input');
    if (firstInput) {
      firstInput.focus();
    }
  };

  return (
    <>
      <ScrollToTop>
        <div className="min-dvh-100">
          <AppPageContentWrapper>
            <div className="mnp-activation w-100">
              {headerContent?.content !== '' && headerContent?.content !== undefined && (
                <>
                  {headerContent?.isHtml ? (
                    <h2
                      className="ek-h2 text-primary mb-40"
                      dangerouslySetInnerHTML={{ __html: sanitizeRichText(headerContent?.content) }}
                    />
                  ) : (
                    <h2 className="ek-h2 text-primary mb-40">{headerContent?.content}</h2>
                  )}
                </>
              )}

              <Formik
                initialValues={{
                  ...phoneNumberActivationForm,
                  oldNumberComplete: '',
                  portingOrder: false
                }}
                validationSchema={phoneNumberValidationsWithMNP}
                onSubmit={(values) => {
                  // split oldNumberComplete into oldNumberPrefix and oldNumber(suffix)
                  const { oldNumberComplete } = values;
                  const oldNumberPrefix = oldNumberComplete.slice(0, 4);
                  const oldNumber = oldNumberComplete.slice(4);
                  
                  const checkAreaCode = areaCode?.find(
                    (code) => code.value === oldNumberPrefix
                  );
                  if (!checkAreaCode) {
                    setWithMNPError(true);
                    return;
                  }
                  setWithMNPError(false);
                  setPhoneNumberActivationForm({
                    ...phoneNumberActivationForm,
                    ...values,
                    oldNumberPrefix,
                    oldNumber // old number suffix/base
                  });
                  setStep3SubStep(2);
                }}>
                {({
                  values: { currentProvider, oldNumberComplete, portingOrder },
                  handleBlur,
                  handleChange,
                  handleSubmit,
                  setFieldValue,
                  touched,
                  errors
                  // eslint-disable-next-line arrow-body-style
                }) => {
                  // BFSG: remove console.log
                  // console.log(errors);
                  return (
                    <Form onSubmit={handleSubmit} noValidate>
                      <DropdownList
                        id="currentProvider"
                        name="currentProvider"
                        label={provideLabelContent?.content}
                        value={currentProvider}
                        placeholder={providePlaceHolderContent?.content}
                        onChange={(callbackValue) => {
                          setWithMNPError(false);
                          setFieldValue('currentProvider', callbackValue);
                        }}
                        onBlur={handleBlur}
                        options={(simProvider || []).map((provider) => ({
                          id: provider.id,
                          label: provider.value
                        }))}
                        valid={touched.currentProvider && !errors.currentProvider && !withMNPError}
                        invalid={(touched.currentProvider && errors.currentProvider) || withMNPError}
                        invalidMessage={
                          touched.currentProvider && errors.currentProvider
                            ? errors.currentProvider
                            : null
                        }
                        isRequired
                        aria-describedby="currentProvider-error"
                      />
                      <div className="space-32" />
                      <FormInputField
                        label={oldNumberLabelContent?.content}
                        placeholder={oldNumberPlaceHolderContent?.content}
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        id="oldNumberComplete"
                        name="oldNumberComplete"
                        value={oldNumberComplete}
                        onChange={(e) => {
                          setWithMNPError(false);
                          handleChange({
                            target: {
                              id: 'oldNumberComplete',
                              name: 'oldNumberComplete',
                              value: e.target.value
                            }
                          });
                        }}
                        onClear={() => {
                          setFieldValue('oldNumberComplete', '');
                          setWithMNPError(false);
                        }}
                        onBlur={handleBlur}
                        // valid={touched.oldNumberComplete && !errors.oldNumberComplete}
                        invalid={(!!touched.oldNumberComplete && !!errors.oldNumberComplete) || withMNPError}
                        invalidMessage={
                          touched.oldNumberComplete && errors.oldNumberComplete
                            ? errors.oldNumberComplete
                            : null
                        }
                        maxLength={16}
                        aria-describedby="oldNumberComplete-error"
                        checkCharRegex={validCharRegex.OLD_NUMBER}
                      />
                      <div className="space-40" />
                      <div className="checkbox-container w-100 text-primary">
                        <ButtonCheckbox
                          name="portingOrder"
                          id="portingOrder-checkbox"
                          value={portingOrder}
                          checkboxContent={checkboxLabelContent}
                          checkboxSize={appCheckboxSize.LARGE}
                          onChange={(isChecked) => {
                            handleChange({
                              target: {
                                id: 'portingOrder',
                                name: 'portingOrder',
                                value: isChecked
                              }
                            });
                          }}
                          onBlur={handleBlur}
                          invalid={!!touched.portingOrder && !!errors.portingOrder}
                          valid={!!touched.portingOrder && !errors.portingOrder}
                          aria-describedby="portingOrder-error"
                        />
                        <CMSLink
                          linkStyle={`${appLinkStyle.PRIMARY}`}
                          iconRight={appIconsPath.ARROW_RIGHT}
                          linkKey="ek_reg_with_mnp_chckbox_lnk1"
                        />
                        {touched.portingOrder && errors.portingOrder ? (
                          <div className="pt-20 ps-32" id="portingOrder-error">
                            <Notification type={appAlert.ERROR} message={errors.portingOrder} />
                          </div>
                        ) : null}
                      </div>

                      {simProviderError || withMNPError && (
                        <div className="form-text mt-40 ps-4" aria-live="polite">
                          <ApiError
                            isGenericApiError={!withMNPError}
                            message={withMNPError ? 'ek_global_mnp_prefix_err' : simProviderError}
                            handleAPIErrorLink={handleAPIErrorLink}
                          />
                        </div>
                      )}
                      <div className="w-100 d-flex justify-content-center">
                        <div className="col-xxl-5 col-lg-5 col-10 px-0 mt-40">
                          <ButtonPrimary
                            iconRight={appIconsPath.ARROW_RIGHT}
                            buttonType={appButtonType.PRIMARY}
                            buttonContent={tA11y('ek_reg_with_mnp_btn1')}
                            label="ek_reg_with_mnp_btn1"
                            type="submit"
                            isLoading={isLoading}
                          />
                        </div>
                      </div>
                      <FocusFirstError />
                    </Form>
                  );
                }}
              </Formik>

              <div className="space-40" />
              <CardSupport />
              <div className="d-flex justify-content-center mt-32">
                <ButtonCancelActivation />
              </div>
            </div>
          </AppPageContentWrapper>
        </div>
      </ScrollToTop>
    </>
  );
}

export default MNPActivation;
